import type { ISearchHit } from "../../../lib/types/search"

/** How the returned hits are ordered. Newest is client-side over this page of hits. */
export type TSearchSort = "relevance" | "newest"

/** One meeting a grouped topic points at. */
export interface ITopicMeetingLink {
  meetingId: string
  subject: string
  date: string
}

/** A row in the results list: one hit, or one topic that showed up in several meetings. */
export type TPresentedHit =
  | { kind: "hit"; hit: ISearchHit }
  | { kind: "topics"; label: string; meetings: ITopicMeetingLink[] }

const normaliseTopic = (text: string): string => text.trim().replace(/\s+/g, " ").toLocaleLowerCase()

const meetingTime = (hit: ISearchHit): number => {
  const parsed = Date.parse(hit.meetingDate)
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY
}

const byNewest = (a: ISearchHit, b: ISearchHit): number => meetingTime(b) - meetingTime(a)

const uniqueMeetings = (hits: readonly ISearchHit[]): ITopicMeetingLink[] => {
  const seen = new Set<string>()
  const meetings: ITopicMeetingLink[] = []
  for (const hit of hits) {
    if (seen.has(hit.meetingId)) continue
    seen.add(hit.meetingId)
    meetings.push({
      meetingId: hit.meetingId,
      subject: hit.meetingSubject,
      date: hit.meetingDate,
    })
  }
  return meetings
}

/**
 * Order hits, then fold identical topic hits (same channel and normalised text)
 * that land in more than one meeting into a single row. A topic that only
 * appears in one meeting stays a normal hit. Other channels are never grouped.
 */
export const presentSearchHits = (hits: readonly ISearchHit[], sort: TSearchSort): TPresentedHit[] => {
  const ordered = sort === "newest" ? [...hits].sort(byNewest) : [...hits]
  const buckets = new Map<string, ISearchHit[]>()
  for (const hit of ordered) {
    if (hit.sourceChannel !== "topics") continue
    const key = normaliseTopic(hit.content)
    const list = buckets.get(key)
    if (list) list.push(hit)
    else buckets.set(key, [hit])
  }

  const emitted = new Set<string>()
  const rows: TPresentedHit[] = []
  for (const hit of ordered) {
    if (hit.sourceChannel !== "topics") {
      rows.push({ kind: "hit", hit })
      continue
    }
    const key = normaliseTopic(hit.content)
    if (emitted.has(key)) continue
    emitted.add(key)
    const bucket = buckets.get(key) ?? [hit]
    const meetings = uniqueMeetings(bucket)
    if (meetings.length < 2) {
      rows.push({ kind: "hit", hit: bucket[0] ?? hit })
      continue
    }
    rows.push({
      kind: "topics",
      label: (bucket[0]?.content ?? hit.content).trim(),
      meetings,
    })
  }
  return rows
}
